import {
  Badge,
  Box,
  Checkbox,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React from "react";
import { GetEvvDevHelpVisitDetailsQuery } from "../../../shared/schema/gql/graphql";
import { VisitInstanceId } from "../../../shared/schema/schema";
import { useEntityLink } from "../../../shared/hooks/useEntityLink";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { EvvStatusCell } from "../../cico-reports/components/EvvStatusCell";
import { SandataPayloadCell } from "../../cico-reports/components/SandataPayloadCell";
import { getEvvCurrentErrors, getEvvLastSubmittedAt } from "../../cico-reports/utils/evv-status";
import { EvvDevHelpDashboardVisit } from "../evv-dev-help.api";

export type EvvDevHelpVisitDetail =
  GetEvvDevHelpVisitDetailsQuery["clockedVisitInstances"]["nodes"][0];

function fullName(
  person: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
) {
  if (!person) return "-";
  return [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ");
}

function safe(format: () => string) {
  try {
    return format();
  } catch {
    return "-";
  }
}

function formatTime(value: unknown) {
  return value ? safe(() => dateFormatter.toTime(value as any)) : "-";
}

/** The header of each group of columns; the group is one cell so it reads as one block. */
function GroupHeader(props: { children: React.ReactNode }) {
  return <Th whiteSpace="nowrap">{props.children}</Th>;
}

export function EvvDevHelpVisitsTable(props: {
  visits: EvvDevHelpDashboardVisit[];
  details: Map<number, EvvDevHelpVisitDetail>;
  isLoadingDetails: boolean;
  selected: Set<number>;
  onToggleVisit: (visitInstanceId: number, checked: boolean) => void;
}) {
  const entityLink = useEntityLink();

  return (
    // Scrolls sideways instead of squeezing the columns: there are a lot of them.
    <Box overflowX="auto">
      <Table size="md" variant="simple">
        <Thead>
          <Tr>
            <Th w="40px" />
            <GroupHeader>Visit</GroupHeader>
            <GroupHeader>Patient / Caregiver</GroupHeader>
            <GroupHeader>Clock in / out</GroupHeader>
            <GroupHeader>EVV</GroupHeader>
            <GroupHeader>Error when flagged</GroupHeader>
            <GroupHeader>Dev help</GroupHeader>
          </Tr>
        </Thead>
        <Tbody>
          {props.visits.map((visit) => {
            const detail = props.details.get(visit.visitInstanceId);
            return (
              <Tr key={visit.visitInstanceId} verticalAlign="top">
                <Td>
                  <Checkbox
                    isChecked={props.selected.has(visit.visitInstanceId)}
                    isDisabled={visit.status === "HANDLED"}
                    onChange={(e) => props.onToggleVisit(visit.visitInstanceId, e.target.checked)}
                  />
                </Td>
                <Td minW="190px">
                  <Link
                    color="blue.500"
                    fontWeight="semibold"
                    onClick={(e) =>
                      entityLink.open({
                        event: e,
                        entity: { type: "VisitInstance", id: visit.visitInstanceId as any },
                      })
                    }
                  >
                    #{visit.visitInstanceId}
                  </Link>
                  <Text>{dateFormatter.toDateTime(visit.visitStartTime)}</Text>
                  <Text color="gray.600">
                    {visit.aggregator}
                    {detail?.serviceCode ? ` · ${detail.serviceCode.code}` : ""}
                  </Text>
                  {detail?.contract && (
                    <Text color="gray.600">{detail.contract.contractType.name}</Text>
                  )}
                </Td>
                <Td minW="190px">
                  {detail === undefined ? (
                    <Text color="gray.500">{props.isLoadingDetails ? "Loading…" : "-"}</Text>
                  ) : (
                    <>
                      <Link
                        color="blue.500"
                        onClick={(e) =>
                          entityLink.open({
                            event: e,
                            entity: { type: "Patient", id: detail.patientId as any },
                          })
                        }
                      >
                        {fullName(detail.patient)}
                      </Link>
                      <Link
                        color="blue.500"
                        display="block"
                        onClick={(e) =>
                          entityLink.open({
                            event: e,
                            entity: { type: "Caregiver", id: detail.caregiverId as any },
                          })
                        }
                      >
                        {fullName(detail.caregiver)}
                      </Link>
                    </>
                  )}
                </Td>
                <Td minW="160px" whiteSpace="nowrap">
                  {detail === undefined ? (
                    "-"
                  ) : (
                    <>
                      <Text>
                        In: {formatTime(detail.clockinTime)}
                        {detail.clockinType ? ` (${detail.clockinType})` : ""}
                      </Text>
                      <Text>
                        Out: {formatTime(detail.clockoutTime)}
                        {detail.clockoutType ? ` (${detail.clockoutType})` : ""}
                      </Text>
                    </>
                  )}
                </Td>
                <Td minW="260px">
                  <EvvCell detail={detail} />
                </Td>
                <Td color="red.600" maxW="320px" whiteSpace="normal">
                  {visit.errorSnapshot.join("; ") || "-"}
                </Td>
                <Td minW="170px">
                  {visit.status === "HANDLED" ? (
                    <Box>
                      <Badge colorScheme="green" fontSize="sm">
                        Handled
                      </Badge>
                      {visit.handledByName !== null && (
                        <Text color="gray.600">by {visit.handledByName}</Text>
                      )}
                      {visit.handledNote !== null && (
                        <Text color="gray.600" whiteSpace="normal">
                          {visit.handledNote}
                        </Text>
                      )}
                    </Box>
                  ) : (
                    <Badge colorScheme="orange" fontSize="sm">
                      Awaiting dev
                    </Badge>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

function EvvCell(props: { detail: EvvDevHelpVisitDetail | undefined }) {
  const { detail } = props;
  if (detail === undefined) return <>-</>;

  const aggregator = detail.contract?.contractType.evvAggregator ?? null;
  const lastSubmittedAt = getEvvLastSubmittedAt(detail as any);
  const currentErrors = getEvvCurrentErrors(detail as any);

  return (
    <Box>
      <Box alignItems="center" display="flex" gap={2}>
        <EvvStatusCell
          clockinTime={detail.clockinTime ? String(detail.clockinTime) : null}
          clockoutTime={detail.clockoutTime ? String(detail.clockoutTime) : null}
          emednySubmission={detail.emednySubmission ?? null}
          evvAggregator={aggregator}
          hhaexchangeSubmission={detail.hhaexchangeSubmission ?? null}
          sandataSubmission={detail.sandataSubmission ?? null}
          tellusSubmission={detail.tellusSubmission ?? null}
        />
        <SandataPayloadCell
          evvAggregator={aggregator}
          hasSandataSubmission={detail.sandataSubmission != null}
          visitInstanceId={detail.id as VisitInstanceId}
        />
      </Box>
      <Text color="gray.600">
        Last submitted:{" "}
        {lastSubmittedAt ? safe(() => dateFormatter.toDateTime(lastSubmittedAt)) : "-"}
      </Text>
      <Text color={currentErrors.length > 0 ? "red.600" : "gray.600"} whiteSpace="normal">
        Current error: {currentErrors.join("; ") || "-"}
      </Text>
    </Box>
  );
}
